/**
 * Affichage d'une personne du registre (team_members) dans les documents.
 *
 * Le contrat est celui du rider/day sheet, documenté et testé :
 *   - NOM  = `nickname` s'il est renseigné, sinon `name`. Un document de tournée
 *     imprime le nom de scène (« Xy »), pas l'état civil (« Alexandre Locher »).
 *   - POSITION = `role` s'il est renseigné, sinon le PREMIER tag qui n'est pas la
 *     catégorie (« mon », « foh », « tm »…). Jamais `category` : elle est un miroir
 *     du 1er tag, pas une position, et l'afficher produirait « technician » pour
 *     tout le monde. Jamais de repli `|| 'crew'` : un poste inconnu reste vide,
 *     une chaîne inventée est pire qu'une cellule vide.
 *
 * Ces helpers sont VOLONTAIREMENT locaux aux documents : les pages de liste
 * (CrewListPage / MusicianListPage) gardent leurs propres libellés, on ne les
 * touche pas — un changement ici ne doit pas modifier ce qu'elles affichent.
 */
export interface PersonLike {
  name?: string | null;
  nickname?: string | null;
  role?: string | null;
  tags?: string[] | null;
  category?: string | null;
  /** Identité Passeport (2026-11-03) : `real_name` = prénom(s), `last_name` = nom.
   *  C'est ce que l'hôtel, la compagnie et le bateau lisent — jamais le nom de scène. */
  real_name?: string | null;
  last_name?: string | null;
}

const clean = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/** Nom imprimé sur un document : nickname sinon name. */
export function personDisplayName(p: PersonLike | null | undefined): string {
  if (!p) return '';
  return clean(p.nickname) || clean(p.name);
}

/**
 * Nom OFFICIEL (passeport) pour les documents qui engagent une réservation :
 * rooming list, vols, listes d'embarquement. Format d'usage des bookings :
 * `NOM, Prénom` — le nom de famille en capitales, le prénom tel que saisi
 * (accents préservés : « VERGÈRES, Johann » ne devient pas « VERGERES »).
 *
 * Replis, dans l'ordre : real_name+last_name → name → nickname → ''. On ne
 * fabrique JAMAIS un nom : une réservation au nom de scène est précisément la
 * « surprise » que cette fonction existe pour éviter.
 */
export function personOfficialName(p: PersonLike | null | undefined): string {
  if (!p) return '';
  const last = clean(p.last_name);
  const first = clean(p.real_name);
  if (last && first) return `${last.toUpperCase()}, ${first}`;
  if (last) return last.toUpperCase();
  if (first) return first;
  return clean(p.name) || clean(p.nickname);
}

/**
 * Position(s) imprimée(s) : **TOUTES** les fonctions, jointes par « , » (2026-11-03).
 * Une personne peut cumuler des rôles — le tour book de Håkon imprime
 * « Johi — Monitor, production manager », pas seulement le premier.
 *
 * Ordre : le `role` d'abord (c'est la fonction principale), puis les tags qui
 * n'apparaissent pas déjà dans ce rôle. La catégorie (`musician` / `crew` / `tech`)
 * n'est JAMAIS imprimée : c'est un classeur, pas une fonction.
 */
export function personPosition(p: PersonLike | null | undefined): string {
  if (!p) return '';
  const cat = clean(p.category).toLowerCase();
  const out: string[] = [];
  const push = (v: string) => {
    const v2 = clean(v);
    if (!v2) return;
    if (v2.toLowerCase() === cat) return;
    if (out.some((x) => x.toLowerCase() === v2.toLowerCase())) return;
    out.push(v2);
  };
  // Les TAGS d'abord : ce sont les fonctions du band (« lights », « guitar tech »,
  // « mon », « production manager »). Le `role` de la fiche ne sert que si la carte
  // n'a aucun tag — sinon on imprimerait deux fois la même fonction sous deux
  // vocabulaires (« LD, lights »).
  const tags = (p.tags || []).map(clean).filter(Boolean);
  for (const tg of tags) push(tg);
  if (out.length === 0) push(clean(p.role));
  // Un `role` peut déjà contenir plusieurs fonctions (« Vocals, Guitar ») : on le
  // garde tel quel quand il n'y a pas de tag.
  return capitalizeFirst(out.join(', '));
}

/** Première lettre en majuscule (« mon, production manager » → « Mon, production manager »). */
export function capitalizeFirst(s: string): string {
  const v = (s || '').trim();
  if (!v) return '';
  return v.charAt(0).toUpperCase() + v.slice(1);
}

/** Une ligne « BAND AND CREW » prête à imprimer. */
export interface CrewRow {
  name: string;
  position: string;
}

/**
 * Résout les personnes d'une date à partir des affectations (`date_assignments`,
 * rendues par l'API sous la forme `rooming`) + le registre de l'équipe.
 *
 * ORDRE (demande utilisateur 2026-11-03) : les MUSICIENS d'abord, puis les autres
 * (« crew » / tech / inconnus), chaque groupe trié par NOM AFFICHÉ (A→Z, insensible
 * à la casse et aux accents). C'est l'ordre d'une page « band and crew » de tour
 * manager : le groupe, puis l'équipe.
 *
 * Une affectation sans fiche connue retombe sur son libellé de date, jamais sur
 * un UUID.
 */
export function crewRowsFromAssignments(
  assignments: Array<{ team_member_id?: string | number | null; role_at_date?: string | null }> | null | undefined,
  members: Array<PersonLike & { id?: string | number | null }> | null | undefined,
): CrewRow[] {
  const seen = new Set<string>();
  const list = members || [];
  const byId = new Map<string, PersonLike & { id?: string | number | null }>();
  for (const m of list) if (m && m.id != null) byId.set(String(m.id), m);

  // Ordre : registre (déjà trié par nom côté serveur) puis affectations inconnues.
  const ordered: Array<{ team_member_id?: string | number | null; role_at_date?: string | null }> = [];
  const assignedIds = new Set((assignments || []).map((a) => String(a.team_member_id)));
  for (const m of list) if (m && m.id != null && assignedIds.has(String(m.id))) ordered.push({ team_member_id: m.id });
  for (const a of assignments || []) if (!byId.has(String(a.team_member_id))) ordered.push(a);

  const sortable: Array<CrewRow & { rank: number }> = [];
  for (const a of ordered) {
    const id = String(a.team_member_id ?? '');
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const m = byId.get(id);
    const name = m ? personDisplayName(m) : clean(a.role_at_date);
    if (!name) continue; // jamais d'UUID dans un document
    sortable.push({ name, position: m ? personPosition(m) : '', rank: crewRank(m) });
  }
  return sortCrewRows(sortable);
}

/** Groupe 0 = musicien, 1 = tout le reste (crew / tech / inconnu). */
const crewRank = (m?: PersonLike | null): number =>
  (clean(m?.category).toLowerCase() === 'musician' ? 0 : 1);

/** Tri commun aux deux sources : musiciens d'abord, puis A→Z par nom affiché. */
function sortCrewRows(rows: Array<CrewRow & { rank: number }>): CrewRow[] {
  rows.sort((x, y) => x.rank - y.rank || x.name.localeCompare(y.name, undefined, { sensitivity: 'base' }));
  return rows.map((r) => ({ name: r.name, position: r.position }));
}

/**
 * Les personnes du SHOW (« People on this show ») — équivalent de `crewRowsFromAssignments`
 * mais pour la vraie source : la sélection du rider du show (`active_musician_ids` ∪
 * `active_crew_ids`), et à défaut les cartes `base_role = main` du groupe (2026-11-03).
 *
 * ⚠️ Ne PAS confondre avec `date_assignments` (= le ROOMING) : un membre peut être sur la
 * route et n'avoir aucune chambre assignée, l'inverse est vrai aussi. BAND AND CREW =
 * « qui est là ce jour », pas « qui dort où ».
 */
export function crewRowsFromSelection(
  ids: Iterable<string | number> | null | undefined,
  members: Array<PersonLike & { id?: string | number | null }> | null | undefined,
  /** Rôles définis POUR CE SHOW (`rider_roles` : un rôle peut n'être vrai que sur une
   *  date). Priorité absolue sur la carte du groupe et sur le registre. */
  roleOverrides?: Record<string, string[]> | null,
): CrewRow[] {
  const list = members || [];
  const byId = new Map<string, PersonLike & { id?: string | number | null }>();
  for (const m of list) if (m && m.id != null) byId.set(String(m.id), m);
  const out: Array<CrewRow & { rank: number }> = [];
  const seen = new Set<string>();
  for (const raw of ids || []) {
    const id = String(raw ?? '');
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const m = byId.get(id);
    if (!m) continue; // id inconnu du registre : on n'invente pas de nom
    const name = personDisplayName(m);
    if (!name) continue;
    const override = (roleOverrides?.[id] || []).map((r) => String(r || '').trim()).filter(Boolean);
    out.push({
      name,
      position: override.length > 0 ? capitalizeFirst(override.join(', ')) : personPosition(m),
      rank: crewRank(m),
    });
  }
  return sortCrewRows(out);
}

/**
 * Trie des NOMS de personnes par ordre alphabétique, insensible aux accents et à
 * la casse (« CAVAZZANA » avant « LOCHER », « é » comme « e »).
 * Utilisé pour les passagers d'un vol : le document doit se lire comme un
 * boarding list, pas dans l'ordre d'insertion des `member_ids` (2026-11-03).
 */
export function sortPeopleNames(names: (string | null | undefined)[]): string[] {
  return names
    .map((n) => String(n ?? '').trim())
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true }));
}
